import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import {
  Plus, Search, FileText, Users, Euro, ChevronRight, X, Copy, Trash2,
  Clock, CheckCircle, XCircle, Send, AlertTriangle
} from 'lucide-react';

export const STATI = {
  BOZZA: { label: 'Bozza', classe: 'bg-gray-100 text-gray-700', icona: FileText },
  INVIATO: { label: 'Inviato', classe: 'bg-blue-100 text-blue-700', icona: Send },
  ACCETTATO: { label: 'Accettato', classe: 'bg-green-100 text-green-700', icona: CheckCircle },
  RIFIUTATO: { label: 'Rifiutato', classe: 'bg-red-100 text-red-700', icona: XCircle },
};

export const euro = (v) =>
  `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const dataIT = (v) =>
  v ? new Date(`${v}T12:00:00`).toLocaleDateString('it-IT') : '—';

const Preventivi = () => {
  const navigate = useNavigate();
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ricerca, setRicerca] = useState('');
  const [filtroStato, setFiltroStato] = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      setPreventivi(await getAll('/preventivi/preventivi/'));
    } catch (error) {
      console.error('Errore caricamento preventivi:', error);
      notify.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare il preventivo ${p.numero}?`)) return;
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/preventivi/preventivi/${p.id}/`);
      notify.update(toastId, { type: 'success', render: 'Preventivo eliminato' });
      carica();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'eliminazione',
      });
    }
  };

  const duplica = async (p, e) => {
    e.stopPropagation();
    const toastId = notify.loading('Duplicazione...');
    try {
      const res = await api.post(`/preventivi/preventivi/${p.id}/duplica/`);
      notify.update(toastId, { type: 'success', render: `Creato ${res.data.numero}` });
      navigate(`/admin/preventivi/${res.data.id}`);
    } catch (error) {
      notify.update(toastId, { type: 'error', render: 'Errore nella duplicazione' });
    }
  };

  const testo = ricerca.trim().toLowerCase();
  const filtrati = preventivi.filter((p) => {
    if (filtroStato && p.stato !== filtroStato) return false;
    if (!testo) return true;
    return [p.numero, p.committente_nome, p.oggetto, p.localita_destinazione]
      .some((v) => (v || '').toLowerCase().includes(testo));
  });

  const conta = (stato) => preventivi.filter((p) => p.stato === stato).length;
  const valoreAccettati = preventivi
    .filter((p) => p.stato === 'ACCETTATO')
    .reduce((a, p) => a + Number(p.totale || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Preventivi</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gite e noleggi occasionali</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/admin/committenti')}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition font-medium text-sm"
            >
              <Users className="w-4 h-4" />
              Committenti
            </button>
            {modificabile && (
              <button
                onClick={() => navigate('/admin/preventivi/nuovo')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Nuovo preventivo
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {['BOZZA', 'INVIATO', 'ACCETTATO'].map((stato) => (
            <button
              key={stato}
              onClick={() => setFiltroStato(filtroStato === stato ? '' : stato)}
              className={`bg-white border rounded-lg px-4 py-3 text-left transition ${
                filtroStato === stato ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <p className="text-xs text-gray-500">{STATI[stato].label}</p>
              <p className="text-2xl font-bold text-gray-900">{conta(stato)}</p>
            </button>
          ))}
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500">Valore accettati</p>
            <p className="text-2xl font-bold text-green-700">{euro(valoreAccettati)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Numero, committente, destinazione..."
              className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
            {ricerca && (
              <button
                onClick={() => setRicerca('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {filtroStato && (
            <button
              onClick={() => setFiltroStato('')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gray-300 rounded-full hover:border-gray-900"
            >
              {STATI[filtroStato].label}
              <X className="w-3 h-3" />
            </button>
          )}
          <span className="text-sm text-gray-500">
            {filtrati.length} {filtrati.length === 1 ? 'preventivo' : 'preventivi'}
          </span>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          ) : filtrati.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {preventivi.length === 0
                  ? 'Nessun preventivo ancora'
                  : 'Nessun preventivo corrisponde alla ricerca'}
              </p>
              {preventivi.length === 0 && modificabile && (
                <button
                  onClick={() => navigate('/admin/preventivi/nuovo')}
                  className="mt-3 text-sm font-medium text-[#1b94ce] hover:underline"
                >
                  Crea il primo preventivo
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtrati.map((p) => {
                const stato = STATI[p.stato] || STATI.BOZZA;
                const Icona = stato.icona;
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/admin/preventivi/${p.id}`)}
                    className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${stato.classe}`}>
                        <Icona className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {p.numero}
                          <span className="ml-2 font-normal text-gray-600">{p.committente_nome}</span>
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {p.oggetto || p.localita_destinazione || 'Senza oggetto'}
                          {p.data_partenza ? ` · partenza ${dataIT(p.data_partenza)}` : ''}
                          {p.passeggeri ? ` · ${p.passeggeri} pax` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {p.scaduto && (
                        <span
                          title={`Validità scaduta il ${dataIT(p.valido_fino)}`}
                          className="hidden sm:flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          scaduto
                        </span>
                      )}
                      <span className={`hidden sm:inline px-2 py-0.5 text-[10px] font-semibold rounded-full ${stato.classe}`}>
                        {stato.label}
                      </span>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{euro(p.totale)}</p>
                        <p className="text-[10px] text-gray-500">{dataIT(p.data_emissione)}</p>
                      </div>
                      {modificabile && (
                        <div className="hidden sm:flex items-center gap-1">
                          <button
                            onClick={(e) => duplica(p, e)}
                            title="Duplica"
                            className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => elimina(p, e)}
                            title="Elimina"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Preventivi;
